import React, { Component } from 'react'

import debounce from 'debounce'

import { isProductionApp } from '../../../util/src'

import { useInactivityRedirect } from './hooks/useInactivityRedirect'

export const TIMER_REFRESH_TIMESTAMP = 'timerRefreshTimestamp'

export const MINUTES = 15

const DEFAULT_SESSION_LIMIT = isProductionApp()
  ? MINUTES
  : Number(sessionStorage.getItem('expireAfter')) || MINUTES // NOTE: sessionStorage just for debug purposes

// TODO: rewrite without mutation using hooks
interface PrivateRouteSessionProps {
  apiVersion: string
  showSessionInactivityDialog(
    onConfirm: () => void,
    onCancel: () => Promise<void>,
  ): void
  refreshToken(): void
  refreshTokenIfNeeded(): void
  isMounted(): boolean
  cleanUpAndRedirect(type: 'login' | 'inactivity'): Promise<void>
  referrer?: string
  organization?: string
}

class PrivateRouteSessionInner extends Component<PrivateRouteSessionProps> {
  warningTimer: NodeJS.Timeout | null = null

  inactivityTimer: NodeJS.Timeout | null = null

  on: () => void = () => undefined

  constructor(props: PrivateRouteSessionProps, context: any) {
    super(props, context)

    this.on = debounce(this.refresher, 250)
  }

  inactivityWarningHandler = () => {
    if (this.warningTimer) {
      clearInterval(this.warningTimer)
    }

    const { showSessionInactivityDialog, refreshToken } = this.props

    this.warningTimer = setInterval(() => {
      showSessionInactivityDialog(
        () => {
          refreshToken()
          this.registerHandlers()
        },
        () => this.props.cleanUpAndRedirect('login'),
      )
    }, (DEFAULT_SESSION_LIMIT - 1) * 60 * 1000)
  }

  inactivityHandler = () => {
    if (this.inactivityTimer) {
      clearInterval(this.inactivityTimer)
    }

    sessionStorage.setItem(TIMER_REFRESH_TIMESTAMP, Date.now().toString())
    this.inactivityTimer = setInterval(() => {
      this.props.cleanUpAndRedirect('inactivity')
    }, DEFAULT_SESSION_LIMIT * 60 * 1000)
  }

  registerHandlers = () => {
    this.inactivityHandler()
    this.inactivityWarningHandler()
  }

  refresher = () => {
    this.props.refreshTokenIfNeeded()

    // necessary because of debounced refresher can be called even after removing the listener
    if (this.props.isMounted()) {
      this.registerHandlers()
    }
  }

  checkTimeout = () => {
    const lastTimerRefreshTimestamp = sessionStorage.getItem(
      TIMER_REFRESH_TIMESTAMP,
    )

    if (lastTimerRefreshTimestamp) {
      const difference = Date.now() - Number(lastTimerRefreshTimestamp)
      const warningDifference = (DEFAULT_SESSION_LIMIT - 1) * 60 * 1000
      const maxAllowedDifference = DEFAULT_SESSION_LIMIT * 60 * 1000

      if (difference > maxAllowedDifference) {
        return this.props.cleanUpAndRedirect('inactivity')
      }

      if (difference > warningDifference) {
        return this.inactivityWarningHandler()
      }
    }
  }

  componentDidMount() {
    document.addEventListener('click', this.on, true)
    document.addEventListener('scroll', this.on, true)
    window.addEventListener('focus', this.checkTimeout, true)
    window.addEventListener('visibilitychange', this.checkTimeout, true)
    this.registerHandlers()
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.on, true)
    document.removeEventListener('scroll', this.on, true)
    window.removeEventListener('focus', this.checkTimeout, true)
    window.removeEventListener('visibilitychange', this.checkTimeout, true)
    if (this.warningTimer !== null) {
      clearTimeout(this.warningTimer)
    }
    if (this.inactivityTimer !== null) {
      clearTimeout(this.inactivityTimer)
    }
    sessionStorage.removeItem(TIMER_REFRESH_TIMESTAMP)
  }

  render() {
    return null
  }
}

export const PrivateRouteSession = (
  props: Omit<PrivateRouteSessionProps, 'cleanUpAndRedirect'>,
) => {
  const { cleanUpAndRedirect } = useInactivityRedirect({
    referrer: props.referrer,
    organization: props.organization,
  })
  return <PrivateRouteSessionInner {...{ cleanUpAndRedirect, ...props }} />
}
